import { type FC, useEffect, useMemo } from "react";

import { OptionCard } from "@/shared/components/ui/OptionCard";
import { RadioGroup } from "@/shared/components/ui/radio-group";
import { FieldError } from "@/shared/components/ui/FieldError";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useUserContacts } from "@/shared/hooks/useUserContacts";

import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";

const MODE_KEY = "admin_confirm_admin_mode";

export const AdminWhoWillServeScreen: FC<FlowCustomComponentProps> = ({
  answers,
  setAnswer,
  errors,
}) => {
  const { user, person } = useAppContext();
  const { contacts, isLoading: contactsLoading } = useUserContacts();
  const decedentName = person?.first_name ?? "the decedent";
  const mode = (answers[MODE_KEY] as string) ?? "";

  const hasCoApplyCandidates = useMemo(() => {
    const userEmail = user?.email?.toLowerCase() ?? null;
    return contacts.some(
      (c) =>
        c.can_serve &&
        c.is_distributee &&
        !(c.email && userEmail && c.email.toLowerCase() === userEmail),
    );
  }, [contacts, user?.email]);

  useEffect(() => {
    if (contactsLoading) return;
    if (!hasCoApplyCandidates && mode === "co") {
      setAnswer(MODE_KEY, "");
    }
  }, [contactsLoading, hasCoApplyCandidates, mode, setAnswer]);

  const handleSelect = (value: string) => {
    setAnswer(MODE_KEY, value);
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <p className="font-sans text-xl font-semibold leading-[30px]">
          Who will serve as administrator?
        </p>
        <p className="text-sm text-secondary tracking-[-0.28px]">
          Choose how you'd like to proceed with {decedentName}'s estate.
        </p>
      </div>

      <RadioGroup
        name="admin_mode"
        value={mode}
        onValueChange={handleSelect}
        className="flex flex-col gap-3"
      >
        <OptionCard
          label="I am applying to serve as sole administrator"
          selected={mode === "sole"}
          onClick={() => handleSelect("sole")}
          variant="radio"
          value="sole"
        />
        {hasCoApplyCandidates && (
          <OptionCard
            label="I am co-applying with another family member"
            selected={mode === "co"}
            onClick={() => handleSelect("co")}
            variant="radio"
            value="co"
          />
        )}
        <OptionCard
          label="Someone else in the family is applying"
          selected={mode === "someone_else"}
          onClick={() => handleSelect("someone_else")}
          variant="radio"
          value="someone_else"
        />
      </RadioGroup>
      <FieldError message={errors?.admin_mode} />
    </div>
  );
};
